/**
 * Title screen, difficulty choice, lobby, and the end-of-match card.
 *
 * Plain DOM. The whole UI is a few hundred lines of markup driven by state
 * changes, which is far less machinery than pulling in a framework for a
 * handful of screens.
 */

import {
  isValidCode,
  Difficulty,
  DIFFICULTY_NAMES,
  DIFFICULTY_BLURBS,
} from '@static-pines/shared';

import { MIC_SVG, MIC_SVG_MUTED } from './icons.js';
import { BUILD_LABEL } from '../version.js';

export interface MenuCallbacks {
  onHost: (name: string, difficulty: Difficulty) => void;
  onJoin: (name: string, code: string) => void;
  onSolo: (name: string, difficulty: Difficulty) => void;
  onStartMatch: () => void;
  onReturnToTitle: () => void;
  /** Rename mid-lobby — link joiners never saw the title screen's name box. */
  onRename: (name: string) => void;
  /** Clicking a player row: your own mutes your mic, another silences them. */
  onTogglePlayerMute: (sessionId: string) => void;
}

interface LobbyPlayer {
  sessionId: string;
  name: string;
  isHost: boolean;
  isYou: boolean;
  connected: boolean;
  /** Lit while this player's voice is coming through the mesh. */
  speaking: boolean;
  /** Your own row: outbound mic muted. Another's: you have silenced them. */
  muted: boolean;
}

const LEVELS: Difficulty[] = [Difficulty.Casual, Difficulty.Normal, Difficulty.Hard];

export class Menu {
  private readonly root: HTMLDivElement;
  private readonly titleScreen: HTMLDivElement;
  private readonly difficultyScreen: HTMLDivElement;
  private readonly lobbyScreen: HTMLDivElement;
  private readonly endScreen: HTMLDivElement;

  private readonly nameInput: HTMLInputElement;
  private readonly codeInput: HTMLInputElement;
  private readonly errorEl: HTMLDivElement;

  private readonly difficultyIntro: HTMLParagraphElement;
  private readonly lobbyCodeEl: HTMLDivElement;
  private readonly shareButton: HTMLButtonElement;
  private readonly lobbyDifficultyEl: HTMLDivElement;
  private readonly lobbyNameInput: HTMLInputElement;
  private readonly lobbyListEl: HTMLDivElement;
  private readonly startButton: HTMLButtonElement;
  private readonly lobbyHintEl: HTMLDivElement;

  private readonly endTitleEl: HTMLDivElement;
  private readonly endBodyEl: HTMLDivElement;

  /** Which button opened the difficulty screen. */
  private pendingMode: 'solo' | 'host' = 'solo';

  /**
   * One stable row element per session id, patched in place.
   *
   * The roster poll calls showLobby every 250ms. Rebuilding the list each
   * time destroyed the row under the cursor four times a second — hover
   * flashed, and any click whose press and release straddled a rebuild hit
   * an element that no longer existed and was silently swallowed.
   */
  private readonly lobbyRows = new Map<
    string,
    { row: HTMLDivElement; label: HTMLSpanElement; mic: HTMLSpanElement; muted: boolean | null }
  >();

  constructor(parent: HTMLElement, private readonly callbacks: MenuCallbacks) {
    this.root = document.createElement('div');
    this.root.id = 'menu';
    parent.appendChild(this.root);

    // ---- Title ---------------------------------------------------------------
    this.titleScreen = screen();

    const title = document.createElement('h1');
    title.textContent = 'STATIC PINES';
    const tagline = paragraph('tagline', 'Something is walking through the forest.');

    this.nameInput = document.createElement('input');
    this.nameInput.placeholder = 'YOUR NAME';
    this.nameInput.maxLength = 18;
    this.nameInput.value = localStorage.getItem('static-pines.name') ?? '';

    this.codeInput = document.createElement('input');
    this.codeInput.placeholder = 'ROOM CODE';
    this.codeInput.maxLength = 6;
    this.codeInput.style.textTransform = 'uppercase';

    // Creating a room picks a difficulty first; joining adopts the room's.
    const soloBtn = button('PLAY ALONE', () => this.showDifficulty('solo'));
    const hostBtn = button('HOST A GROUP', () => this.showDifficulty('host'));
    const joinBtn = button('JOIN WITH CODE', () => {
      const code = this.codeInput.value.trim().toUpperCase();
      if (!isValidCode(code)) {
        this.showError('That room code does not look right — six letters and digits.');
        return;
      }
      this.callbacks.onJoin(this.name, code);
    });

    this.errorEl = document.createElement('div');
    this.errorEl.className = 'error';

    const controls = document.createElement('div');
    controls.className = 'controls-note';
    controls.innerHTML =
      'WASD move &nbsp;·&nbsp; SHIFT run &nbsp;·&nbsp; CTRL crouch &nbsp;·&nbsp; ' +
      'F flashlight &nbsp;·&nbsp; R radio &nbsp;·&nbsp; E interact<br />' +
      '1-6 or WHEEL select &nbsp;·&nbsp; LEFT CLICK use &nbsp;·&nbsp; Q drop<br />' +
      'SPACE leave ladder / hold breath &nbsp;·&nbsp; ESC pause';

    /**
     * Which build this is, under the controls.
     *
     * Everyone plays whatever the server last deployed, so "it does that on
     * mine" needs a number both people can compare. Sited on the title screen
     * because that is the one screen every player passes through, and kept
     * quiet enough to be furniture until somebody goes looking for it.
     */
    const build = document.createElement('div');
    build.className = 'build-version';
    build.textContent = BUILD_LABEL;

    this.titleScreen.append(
      title,
      tagline,
      this.nameInput,
      soloBtn,
      hostBtn,
      spacer(),
      this.codeInput,
      joinBtn,
      this.errorEl,
      controls,
      build,
    );
    this.root.appendChild(this.titleScreen);

    // ---- Difficulty ----------------------------------------------------------
    this.difficultyScreen = screen();

    const difficultyTitle = document.createElement('h2');
    difficultyTitle.textContent = 'DIFFICULTY';
    this.difficultyIntro = paragraph('tagline', '');

    this.difficultyScreen.append(difficultyTitle, this.difficultyIntro);

    const remembered = Number(localStorage.getItem('static-pines.difficulty'));
    const initial = LEVELS.includes(remembered as Difficulty)
      ? (remembered as Difficulty)
      : Difficulty.Normal;

    for (const level of LEVELS) {
      const option = document.createElement('button');
      option.className = 'difficulty-option';
      if (level === initial) option.classList.add('remembered');

      const label = document.createElement('div');
      label.className = 'difficulty-name';
      label.textContent = DIFFICULTY_NAMES[level].toUpperCase();

      const blurb = document.createElement('div');
      blurb.className = 'difficulty-blurb';
      blurb.textContent = DIFFICULTY_BLURBS[level];

      option.append(label, blurb);
      option.addEventListener('click', () => this.choose(level));
      this.difficultyScreen.appendChild(option);
    }

    const backBtn = button('BACK', () => this.showTitle());
    backBtn.className = 'secondary';
    this.difficultyScreen.appendChild(backBtn);

    this.root.appendChild(this.difficultyScreen);

    // ---- Lobby ---------------------------------------------------------------
    this.lobbyScreen = screen();

    const lobbyTitle = document.createElement('h2');
    lobbyTitle.textContent = 'LOBBY';

    this.lobbyCodeEl = document.createElement('div');
    this.lobbyCodeEl.className = 'room-code';
    this.lobbyCodeEl.addEventListener('click', () => {
      void navigator.clipboard?.writeText(this.lobbyCodeEl.textContent ?? '');
      this.lobbyCodeEl.classList.add('copied');
      window.setTimeout(() => this.lobbyCodeEl.classList.remove('copied'), 900);
    });

    const codeHint = paragraph('tagline', 'Share this code. Click it to copy.');

    /**
     * The platform share sheet, where the platform has one. This is the whole
     * join flow on a phone: the shared URL carries the room code in its hash,
     * and opening it drops the friend straight into this lobby. Hidden where
     * navigator.share does not exist — click-to-copy already covers desktop.
     */
    this.shareButton = button('SHARE INVITE', () => {
      const code = this.lobbyCodeEl.textContent ?? '';
      void navigator
        .share({
          title: 'STATIC PINES',
          text: `Join my forest — room code ${code}`,
          url: `${location.origin}${location.pathname}#${code}`,
        })
        // Dismissing the sheet rejects with AbortError; that is not an error.
        .catch(() => undefined);
    });
    if (typeof navigator.share !== 'function') this.shareButton.style.display = 'none';

    this.lobbyDifficultyEl = document.createElement('div');
    this.lobbyDifficultyEl.className = 'lobby-difficulty';

    /**
     * Rename in the lobby. A friend who opened a shared link never met the
     * title screen's name box — this is their only chance to not be
     * "Survivor". Committing on change (blur/Enter) rather than per keypress
     * keeps half-typed names off everyone else's screen.
     */
    this.lobbyNameInput = document.createElement('input');
    this.lobbyNameInput.className = 'lobby-name';
    this.lobbyNameInput.placeholder = 'YOUR NAME';
    this.lobbyNameInput.maxLength = 18;
    this.lobbyNameInput.addEventListener('change', () => {
      const name = this.lobbyNameInput.value.trim();
      if (!name) return;
      localStorage.setItem('static-pines.name', name);
      this.callbacks.onRename(name);
    });

    this.lobbyListEl = document.createElement('div');
    this.lobbyListEl.className = 'player-list';

    this.startButton = button('BEGIN', () => this.callbacks.onStartMatch());
    this.lobbyHintEl = document.createElement('div');
    this.lobbyHintEl.className = 'tagline';

    const leaveBtn = button('LEAVE', () => this.callbacks.onReturnToTitle());
    leaveBtn.className = 'secondary';

    this.lobbyScreen.append(
      lobbyTitle,
      this.lobbyCodeEl,
      codeHint,
      this.shareButton,
      this.lobbyDifficultyEl,
      this.lobbyNameInput,
      this.lobbyListEl,
      this.startButton,
      this.lobbyHintEl,
      leaveBtn,
    );
    this.root.appendChild(this.lobbyScreen);

    // ---- End card ------------------------------------------------------------
    this.endScreen = screen();

    this.endTitleEl = document.createElement('div');
    this.endTitleEl.className = 'end-title';
    this.endBodyEl = document.createElement('div');
    this.endBodyEl.className = 'end-body';

    this.endScreen.append(
      this.endTitleEl,
      this.endBodyEl,
      button('RETURN TO TITLE', () => this.callbacks.onReturnToTitle()),
    );
    this.root.appendChild(this.endScreen);

    this.showTitle();
  }

  private get name(): string {
    const value = this.nameInput.value.trim() || 'Survivor';
    localStorage.setItem('static-pines.name', value);
    return value;
  }

  private choose(level: Difficulty): void {
    localStorage.setItem('static-pines.difficulty', String(level));
    if (this.pendingMode === 'solo') this.callbacks.onSolo(this.name, level);
    else this.callbacks.onHost(this.name, level);
  }

  private showDifficulty(mode: 'solo' | 'host'): void {
    this.pendingMode = mode;
    this.difficultyIntro.textContent =
      mode === 'solo' ? 'Alone in the forest.' : 'Up to six of you.';

    this.only(this.difficultyScreen);
  }

  /** Show exactly one screen. */
  private only(target: HTMLDivElement): void {
    this.root.classList.remove('hidden');
    for (const s of [this.titleScreen, this.difficultyScreen, this.lobbyScreen, this.endScreen]) {
      s.classList.toggle('hidden', s !== target);
    }
  }

  showError(message: string): void {
    this.errorEl.textContent = message;
  }

  showTitle(): void {
    this.only(this.titleScreen);
    this.errorEl.textContent = '';
  }

  showLobby(
    code: string,
    players: LobbyPlayer[],
    youAreHost: boolean,
    difficulty: Difficulty,
    towersRequired: number,
  ): void {
    /**
     * Prime the rename box only on the way IN to the lobby. showLobby is
     * re-run by the 250ms roster poll, and re-assigning the value on every
     * poll would clobber whatever the player is midway through typing.
     */
    if (this.lobbyScreen.classList.contains('hidden')) {
      this.lobbyNameInput.value = localStorage.getItem('static-pines.name') ?? '';
    }

    this.only(this.lobbyScreen);

    this.lobbyCodeEl.textContent = code;

    // Joiners cannot pick the difficulty, so tell them what they walked into.
    this.lobbyDifficultyEl.textContent =
      `${DIFFICULTY_NAMES[difficulty]?.toUpperCase() ?? 'NORMAL'}  ·  ${towersRequired} TOWERS`;

    const seen = new Set<string>();
    players.forEach((player, index) => {
      seen.add(player.sessionId);

      let entry = this.lobbyRows.get(player.sessionId);
      if (!entry) {
        const row = document.createElement('div');
        row.className = 'player-row clickable';

        const label = document.createElement('span');

        // Every row gets the mic so the column reads as a column; a talking
        // player's glows, a muted one wears the slash.
        const mic = document.createElement('span');
        mic.className = 'mic-indicator';

        // Your row silences you for everyone; another's silences them for
        // you. The id is captured once — the element never rebuilds, so the
        // listener can never go stale.
        const sessionId = player.sessionId;
        row.addEventListener('click', () => this.callbacks.onTogglePlayerMute(sessionId));

        row.append(label, mic);
        entry = { row, label, mic, muted: null };
        this.lobbyRows.set(sessionId, entry);
      }

      // Patch in place. Assignments that would not change anything are
      // skipped so the browser never invalidates the hovered element.
      const text = player.isHost ? `${player.name} (host)` : player.name;
      const labelText = player.isYou ? `▸ ${text}` : text;
      if (entry.label.textContent !== labelText) entry.label.textContent = labelText;

      const title = player.isYou ? 'Mute / unmute your microphone' : `Mute ${player.name} for you`;
      if (entry.row.title !== title) entry.row.title = title;

      entry.row.classList.toggle('disconnected', !player.connected);
      if (entry.muted !== player.muted) {
        entry.mic.innerHTML = player.muted ? MIC_SVG_MUTED : MIC_SVG;
        entry.muted = player.muted;
      }
      entry.mic.classList.toggle('muted', player.muted);
      entry.mic.classList.toggle('talking', !player.muted && player.speaking);

      // Only touch the DOM tree when a row is new or out of order — moving a
      // node re-triggers hover just like rebuilding it would.
      if (this.lobbyListEl.children[index] !== entry.row) {
        this.lobbyListEl.insertBefore(entry.row, this.lobbyListEl.children[index] ?? null);
      }
    });

    for (const [sessionId, entry] of this.lobbyRows) {
      if (!seen.has(sessionId)) {
        entry.row.remove();
        this.lobbyRows.delete(sessionId);
      }
    }

    this.startButton.style.display = youAreHost ? '' : 'none';
    this.lobbyHintEl.textContent = youAreHost
      ? `${players.length} of 6 · you can start whenever`
      : 'Waiting for the host to begin…';
  }

  showEnd(won: boolean, reason: string, survivors: string[]): void {
    this.only(this.endScreen);

    this.endTitleEl.textContent = won ? 'EXTRACTED' : 'LOST TO THE FOREST';
    this.endTitleEl.classList.toggle('won', won);

    const survivorLine =
      survivors.length > 0 ? `Survivors: ${survivors.join(', ')}` : 'There were no survivors.';
    this.endBodyEl.textContent = `${reason}\n${survivorLine}`;
  }

  hide(): void {
    this.root.classList.add('hidden');
  }
}

function screen(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'screen hidden';
  return el;
}

function paragraph(className: string, text: string): HTMLParagraphElement {
  const el = document.createElement('p');
  el.className = className;
  el.textContent = text;
  return el;
}

function button(label: string, onClick: () => void): HTMLButtonElement {
  const el = document.createElement('button');
  el.textContent = label;
  el.addEventListener('click', onClick);
  return el;
}

function spacer(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'spacer';
  return el;
}
